<template>
  <manual-page
    title="Reportes"
    description="Guia completa para consultar y analizar los reportes del sistema."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'biweekly' }"
    action-text="Ver reporte bisemanal"
  >
    <!-- Overview -->
    <h2>Vista General de Reportes</h2>
    <p>
      El modulo de reportes te proporciona informacion detallada sobre el desempeño
      del negocio. Desde aqui puedes analizar ventas, operaciones, pagos y
      comportamiento de clientes con diferentes niveles de detalle.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Reportes disponibles:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Bisemanal:</strong> Analisis de ventas cada dos semanas</li>
        <li><strong>Mensual:</strong> Resumen completo del mes</li>
        <li><strong>Salud de Pagos:</strong> Estado de cobros y problemas de pago</li>
        <li><strong>Operativo:</strong> Metricas de produccion y entrega</li>
        <li><strong>Salud de Clientes:</strong> Retencion, churn y comportamiento</li>
      </ul>
    </div>

    <!-- Biweekly Report -->
    <h2>Reporte Bisemanal</h2>
    <p>
      El reporte bisemanal ofrece un analisis de ventas y operaciones cada dos semanas.
      Es ideal para un seguimiento frecuente sin esperar al cierre de mes.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Metricas incluidas</h3>
          <p class="text-gray-600 mb-2">Este reporte muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Ventas totales:</strong> Ingresos de la quincena</li>
            <li><strong>Ordenes procesadas:</strong> Cantidad de pedidos completados</li>
            <li><strong>Nuevos clientes:</strong> Registros en el periodo</li>
            <li><strong>Planes activados:</strong> Nuevos planes que iniciaron</li>
            <li><strong>Planes cancelados:</strong> Planes dados de baja</li>
            <li><strong>Comparativa:</strong> vs. quincena anterior</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Revisa el reporte bisemanal cada viernes para tener
        una imagen clara de como va la operacion y poder reaccionar rapidamente.
      </p>
    </div>

    <!-- Monthly Report -->
    <h2>Reporte Mensual</h2>
    <p>
      El reporte mensual proporciona un analisis completo del mes, incluyendo
      metricas financieras, operativas y de crecimiento.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Contenido del reporte</h3>
          <p class="text-gray-600 mb-2">Incluye:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Resumen financiero:</strong> Ingresos, egresos, balance</li>
            <li><strong>Comparativa:</strong> Ingresos vs. egresos del mes</li>
            <li><strong>Crecimiento:</strong> Variacion de clientes activos</li>
            <li><strong>Por sucursal:</strong> Desglose de metricas por sucursal</li>
            <li><strong>Tendencias:</strong> Comparacion con meses anteriores</li>
            <li><strong>Proyecciones:</strong> Estimados para el siguiente mes</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Uso recomendado</h3>
          <p class="text-gray-600">
            Revisa este reporte al inicio de cada mes para evaluar el desempeño
            del mes anterior y ajustar estrategias segun sea necesario.
          </p>
        </div>
      </div>
    </div>

    <!-- Payment Health Report -->
    <h2>Salud de Pagos</h2>
    <p>
      El reporte de salud de pagos analiza el estado de los cobros, identificando
      problemas y oportunidades para mejorar la tasa de exito.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Metricas de pagos</h3>
          <p class="text-gray-600 mb-2">El reporte incluye:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Tasa de exito:</strong> Porcentaje de cobros exitosos</li>
            <li><strong>Cobros fallidos:</strong> Cantidad y monto de rechazos</li>
            <li><strong>Motivos de fallo:</strong> Por que fallan los cobros</li>
            <li><strong>Reintentos:</strong> Efectividad de los reintentos automaticos</li>
            <li><strong>Por metodo:</strong> Exito por tipo de tarjeta/metodo</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Comparativo por sucursal</h3>
          <p class="text-gray-600">
            Compara la salud de pagos entre franquicias para identificar
            si alguna tiene problemas especificos que requieran atencion.
          </p>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Atencion:</strong> Una tasa de exito menor al 95% requiere investigacion.
        Contacta a los clientes con pagos fallidos para actualizar sus metodos de pago.
      </p>
    </div>

    <!-- Operational Report -->
    <h2>Reporte Operativo</h2>
    <p>
      El reporte operativo se enfoca en las metricas de produccion y entrega,
      ayudando a optimizar la operacion diaria.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Metricas operativas</h3>
          <p class="text-gray-600 mb-2">Incluye:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Ordenes procesadas:</strong> Total del periodo</li>
            <li><strong>Tiempo de preparacion:</strong> Promedio por orden</li>
            <li><strong>Entregas a tiempo:</strong> Porcentaje de puntualidad</li>
            <li><strong>Capacidad utilizada:</strong> Uso de capacidad por sucursal</li>
            <li><strong>Cuellos de botella:</strong> Identificacion de retrasos</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Uso para optimizacion</h3>
          <p class="text-gray-600">
            Usa este reporte para identificar ineficiencias en la operacion
            y tomar decisiones sobre personal, rutas o capacidad.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Si una sucursal consistentemente opera cerca de su
        capacidad maxima, considera expandir o redistribuir clientes.
      </p>
    </div>

    <!-- Customer Health Report -->
    <h2>Salud de Clientes</h2>
    <p>
      El reporte de salud de clientes analiza el comportamiento de los clientes,
      retencion y abandono para mejorar la satisfaccion y reducir cancelaciones.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Metricas de retencion</h3>
          <p class="text-gray-600 mb-2">El reporte muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Tasa de retencion:</strong> Porcentaje de clientes que continuan</li>
            <li><strong>Churn rate:</strong> Tasa de cancelacion mensual</li>
            <li><strong>LTV:</strong> Valor de vida promedio del cliente</li>
            <li><strong>Tiempo promedio:</strong> Duracion promedio de planes</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Motivos de cancelacion</h3>
          <p class="text-gray-600 mb-2">Analiza por que los clientes cancelan:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Precio</li>
            <li>Calidad del producto</li>
            <li>Problemas de entrega</li>
            <li>Cambio de situacion del cliente</li>
            <li>Otros motivos</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acciones recomendadas</h3>
          <p class="text-gray-600">
            Usa esta informacion para diseñar estrategias de retencion:
            descuentos, mejoras de servicio, o comunicacion proactiva con
            clientes en riesgo de abandonar.
          </p>
        </div>
      </div>
    </div>

    <!-- Filters and Export -->
    <h2>Filtros y Exportacion</h2>
    <p>
      Todos los reportes pueden personalizarse con filtros y exportarse
      para analisis externo.
    </p>

    <h3>Opciones de Filtrado</h3>
    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Filtros disponibles:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Periodo:</strong> Selecciona rango de fechas especifico</li>
        <li><strong>Sucursal:</strong> Una sucursal, varias, o todas</li>
        <li><strong>Comparativa:</strong> Activa comparacion con periodo anterior</li>
        <li><strong>Agrupacion:</strong> Por dia, semana o mes</li>
      </ul>
    </div>

    <h3>Exportar Reportes</h3>
    <p>
      Puedes descargar los reportes para compartirlos o analizarlos
      con otras herramientas.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Formatos disponibles</h3>
          <p class="text-gray-600 mb-2">Exporta en:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Excel:</strong> Para analisis en hojas de calculo</li>
            <li><strong>PDF:</strong> Para presentaciones e impresion</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Como exportar</h3>
          <p class="text-gray-600">
            Aplica los filtros deseados, luego haz clic en el boton de
            exportacion y selecciona el formato. La descarga iniciara automaticamente.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Exporta el reporte mensual en PDF para compartirlo
        con socios o inversionistas. Usa Excel si necesitas hacer calculos adicionales.
      </p>
    </div>

    <!-- Best Practices -->
    <h2>Buenas Practicas</h2>
    <ul>
      <li><strong>Revision regular:</strong> Establece una rutina para revisar reportes (semanal, quincenal, mensual)</li>
      <li><strong>Comparar periodos:</strong> Siempre activa la comparativa para ver tendencias</li>
      <li><strong>Enfocarse en metricas clave:</strong> No te pierdas en demasiados numeros, prioriza las metricas que importan</li>
      <li><strong>Tomar accion:</strong> Los reportes son utiles solo si generan acciones de mejora</li>
      <li><strong>Compartir con el equipo:</strong> Comunica los resultados a las personas relevantes</li>
      <li><strong>Documentar aprendizajes:</strong> Registra que acciones funcionaron y cuales no</li>
    </ul>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Recordatorio:</strong> Los reportes muestran datos historicos. Usaos para
        tomar decisiones, pero complementalos con observacion directa de la operacion.
      </p>
    </div>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualReports",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualAccounting", label: "Contabilidad" },
        { route: "manualOrders", label: "Ordenes" },
        { route: "manualFranchises", label: "Franquicias" },
        { route: "manualPlans", label: "Planes" },
      ],
    };
  },
};
</script>
